import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

// useUrlState — the URL-state MECHANISM for G-25 (docs/prd/DOD.md §4, story
// 1G-13): "filters / sort / page / cursor are URL-serializable on every list
// page; contract test: navigating to an encoded-state URL renders that
// state." This hook is the shared primitive every list page composes to get
// that for free — a deep link with query params hydrates the page's filter/
// sort/page/cursor state on first render, and every state change round-trips
// back into the URL (so the address bar is always a valid, shareable,
// back/forward-navigable snapshot).
//
// This story lands the mechanism + wires ONE real page as proof
// (ItemsListPage's status filter — src/pages/ItemsListPage.tsx). Migrating
// every other list page's ad-hoc useState to this hook is the separate sweep
// story referenced by 1G-13 ("per-wave for search / DS / URL-state").
//
// Design: a small codec per field (parse raw string -> typed value, serialize
// typed value -> string | null) rather than a single JSON blob in one query
// param — keeps the URL human-readable/shareable (?status=in_progress&page=2)
// and lets each field own its own default/omission rule (e.g. omit the key
// entirely when the value equals the default, so the "no filter" URL stays
// clean instead of always carrying `?status=all`).

export type UrlStateField<T> = {
  /** Parse the raw query-string value (null if the key is absent) into T. */
  parse: (raw: string | null) => T;
  /** Serialize T back to a query-string value; return null to omit the key. */
  serialize: (value: T) => string | null;
};

export type UrlStateSchema<S extends Record<string, unknown>> = {
  [K in keyof S]: UrlStateField<S[K]>;
};

export type SetUrlState<S extends Record<string, unknown>> = (
  patch: Partial<S>,
  options?: { replace?: boolean }
) => void;

/**
 * Sync a list page's filter/sort/page/cursor state to the URL query string.
 *
 * `schema` should be a stable, module-level object (defined once outside the
 * component) mapping each state key to a `{ parse, serialize }` codec — see
 * the `urlState*` field helpers below for common cases. The returned state is
 * derived fresh from `searchParams` on every render, so a deep link (or
 * browser back/forward) that changes the URL re-derives the matching state
 * with no extra effect/sync required.
 */
export function useUrlState<S extends Record<string, unknown>>(
  schema: UrlStateSchema<S>
): [S, SetUrlState<S>] {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(() => {
    const result = {} as S;
    for (const key of Object.keys(schema) as Array<keyof S>) {
      result[key] = schema[key].parse(searchParams.get(String(key)));
    }
    return result;
  }, [searchParams, schema]);

  const setState = useCallback<SetUrlState<S>>(
    (patch, options) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const key of Object.keys(patch) as Array<keyof S>) {
            const field = schema[key];
            const value = patch[key];
            const serialized = value === undefined ? null : field.serialize(value as S[typeof key]);
            if (serialized === null) {
              next.delete(String(key));
            } else {
              next.set(String(key), serialized);
            }
          }
          return next;
        },
        { replace: options?.replace ?? true }
      );
    },
    [schema, setSearchParams]
  );

  return [state, setState];
}

// --- Common field codecs -----------------------------------------------

/** A plain string field; omits the key when the value equals `defaultValue`. */
export function urlStateString(defaultValue = ''): UrlStateField<string> {
  return {
    parse: (raw) => raw ?? defaultValue,
    serialize: (value) => (value === defaultValue ? null : value),
  };
}

/** An enum-constrained field; falls back to `defaultValue` on a missing/unknown value. */
export function urlStateEnum<T extends string>(values: readonly T[], defaultValue: T): UrlStateField<T> {
  return {
    parse: (raw) => (raw && (values as readonly string[]).includes(raw) ? (raw as T) : defaultValue),
    serialize: (value) => (value === defaultValue ? null : value),
  };
}

/** An integer field (e.g. `page`); omits the key at `defaultValue`, clamps invalid input to it. */
export function urlStateInt(defaultValue = 0): UrlStateField<number> {
  return {
    parse: (raw) => {
      if (raw === null) return defaultValue;
      const n = Number.parseInt(raw, 10);
      return Number.isFinite(n) ? n : defaultValue;
    },
    serialize: (value) => (value === defaultValue ? null : String(value)),
  };
}

/** An opaque cursor/token field (e.g. a pagination cursor); omitted when unset. */
export function urlStateCursor(): UrlStateField<string | null> {
  return {
    parse: (raw) => raw,
    serialize: (value) => (value === null || value === undefined ? null : value),
  };
}
